import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  User, Settings, Shield, Moon, Sun, Monitor, LogOut, X,
  CheckCircle2, KeyRound, Smartphone, ShieldCheck, ChevronRight,
  Lock, Bell, Network, Users, AppWindow, RefreshCw, AlertCircle
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export default function MobileProfileSettingsModal({ isOpen, onClose, onOpenMobileInstall }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const [displayName, setDisplayName] = useState(user?.name || user?.email?.split('@')[0] || 'Nikhil');
  const [autoLockTime, setAutoLockTime] = useState(() => localStorage.getItem('ciphervault_autolock') || '15');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Top-to-bottom slide / swipe down gesture states
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const currentDragY = useRef(0);
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDragY(0);
      setIsDragging(false);
      currentDragY.current = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    const isHandle = e.target.closest('.mobile-sheet-pull-handle') || e.target.closest('.mobile-profile-header');
    const isAtTop = !bodyRef.current || bodyRef.current.scrollTop <= 2;
    if (isHandle || isAtTop) {
      touchStartY.current = touch.clientY;
      setIsDragging(true);
      currentDragY.current = 0;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaY = touch.clientY - touchStartY.current;
    if (deltaY > 0) {
      currentDragY.current = deltaY;
      setDragY(deltaY);
    } else {
      currentDragY.current = 0;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (currentDragY.current > 75) {
      // User slid top-to-bottom > 75px: dismiss modal
      onClose();
    }
    setDragY(0);
    currentDragY.current = 0;
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    localStorage.setItem('ciphervault_display_name', displayName);
    localStorage.setItem('ciphervault_autolock', autoLockTime);
    setSaveSuccess(true);
    if (navigator.vibrate) navigator.vibrate(30);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      onClose();
      navigate('/login');
    } catch (e) {
      setLoggingOut(false);
    }
  };

  const content = (
    <div
      className="mobile-profile-overlay animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={sheetRef}
        className="mobile-profile-sheet animate-slide-up glass-panel"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          touchAction: 'pan-y'
        }}
      >
        {/* iOS Handle (swipeable) */}
        <div
          className="mobile-sheet-pull-handle"
          title="Drag down to close"
        />

        {/* Header */}
        <div className="mobile-profile-header">
          <div className="profile-header-user-info">
            <img
              src={`https://ui-avatars.com/api/?name=${user?.email || 'User'}&background=3b82f6&color=fff&bold=true`}
              alt="Profile avatar"
              className="profile-sheet-avatar"
            />
            <div>
              <div className="profile-sheet-name-row">
                <h4>{displayName || 'Vault User'}</h4>
                <span className={`badge ${isAdmin ? 'badge-primary' : 'badge-secondary'}`}>
                  {user?.role || 'MEMBER'}
                </span>
              </div>
              <p className="profile-sheet-email">{user?.email || 'user@example.com'}</p>
            </div>
          </div>
          <button
            type="button"
            className="mobile-explore-clear-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        {/* Content Body */}
        <div className="mobile-profile-body" ref={bodyRef}>
          {saveSuccess && (
            <div className="profile-save-alert animate-slide-down">
              <CheckCircle2 size={16} className="text-success" />
              <span>Profile and security preferences saved successfully!</span>
            </div>
          )}

          {/* Quick Edit Profile */}
          <form onSubmit={handleSaveProfile} className="profile-edit-section glass-panel">
            <h5 className="profile-section-label">Account Details</h5>
            <div className="profile-input-group">
              <label>Display Name</label>
              <input
                type="text"
                className="profile-text-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>

            <div className="profile-input-group">
              <label>Auto-Lock Inactivity Timeout</label>
              <select
                className="profile-select-input"
                value={autoLockTime}
                onChange={(e) => setAutoLockTime(e.target.value)}
              >
                <option value="5">5 Minutes</option>
                <option value="15">15 Minutes (Default)</option>
                <option value="30">30 Minutes</option>
                <option value="60">1 Hour</option>
                <option value="never">Never (Not Recommended)</option>
              </select>
            </div>

            <button type="submit" className="btn btn-sm btn-primary profile-save-btn">
              <span>Save Changes</span>
            </button>
          </form>

          {/* iOS Theme Switcher Segmented Control */}
          <div className="profile-theme-section glass-panel">
            <h5 className="profile-section-label">Interface Appearance</h5>
            <div className="ios-segmented-theme-control">
              <button
                type="button"
                className={`theme-segment-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <Sun size={15} />
                <span>Light</span>
              </button>
              <button
                type="button"
                className={`theme-segment-btn ${theme === 'system' ? 'active' : ''}`}
                onClick={() => setTheme('system')}
              >
                <Monitor size={15} />
                <span>System</span>
              </button>
              <button
                type="button"
                className={`theme-segment-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <Moon size={15} />
                <span>Dark</span>
              </button>
            </div>
          </div>

          {/* Security & System Shortcuts */}
          <div className="profile-menu-links-group glass-panel">
            <h5 className="profile-section-label">Security & Workspaces</h5>

            <button
              type="button"
              className="profile-menu-row"
              onClick={() => { onClose(); navigate('/security'); }}
            >
              <div className="menu-row-left">
                <div className="menu-row-icon" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                  <ShieldCheck size={16} />
                </div>
                <span>Security Overview & 2FA</span>
              </div>
              <ChevronRight size={15} className="text-secondary" />
            </button>

            <button
              type="button"
              className="profile-menu-row"
              onClick={() => { onClose(); navigate('/settings'); }}
            >
              <div className="menu-row-left">
                <div className="menu-row-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Settings size={16} />
                </div>
                <span>Full System Settings</span>
              </div>
              <ChevronRight size={15} className="text-secondary" />
            </button>

            <button
              type="button"
              className="profile-menu-row"
              onClick={() => { onClose(); navigate('/sessions'); }}
            >
              <div className="menu-row-left">
                <div className="menu-row-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                  <Network size={16} />
                </div>
                <span>Active Sessions & Devices</span>
              </div>
              <ChevronRight size={15} className="text-secondary" />
            </button>

            {isAdmin && (
              <>
                <button
                  type="button"
                  className="profile-menu-row"
                  onClick={() => { onClose(); navigate('/admin/users'); }}
                >
                  <div className="menu-row-left">
                    <div className="menu-row-icon" style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
                      <Users size={16} />
                    </div>
                    <span>User Management</span>
                  </div>
                  <ChevronRight size={15} className="text-secondary" />
                </button>

                <button
                  type="button"
                  className="profile-menu-row"
                  onClick={() => { onClose(); navigate('/admin/applications'); }}
                >
                  <div className="menu-row-left">
                    <div className="menu-row-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                      <AppWindow size={16} />
                    </div>
                    <span>Applications & Suites</span>
                  </div>
                  <ChevronRight size={15} className="text-secondary" />
                </button>
              </>
            )}

            <button
              type="button"
              className="profile-menu-row"
              onClick={() => {
                onClose();
                if (onOpenMobileInstall) onOpenMobileInstall();
              }}
            >
              <div className="menu-row-left">
                <div className="menu-row-icon" style={{ background: 'rgba(6, 182, 212, 0.12)', color: '#06b6d4' }}>
                  <Smartphone size={16} />
                </div>
                <span>Install Native App (iOS / Android)</span>
              </div>
              <ChevronRight size={15} className="text-secondary" />
            </button>
          </div>
        </div>

        {/* Footer Logout */}
        <div className="mobile-profile-footer">
          <button
            type="button"
            className="btn btn-danger btn-block mobile-logout-btn"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            <LogOut size={16} />
            <span>{loggingOut ? 'Signing out...' : 'Sign Out of CipherVault'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
